import React from 'react';
import { Smartphone, CreditCard, PiggyBank, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Personal = () => {
  const services = [
    {
      icon: Smartphone,
      title: "Mobile Banking",
      description: "Bank on the go with our secure mobile app. Check balances, transfer funds, and pay bills anytime, anywhere."
    },
    {
      icon: CreditCard,
      title: "Debit & Credit Cards",
      description: "Enjoy convenient and secure payment options with our range of debit and credit card solutions."
    },
    {
      icon: PiggyBank,
      title: "Savings Accounts",
      description: "Grow your wealth with competitive interest rates and flexible savings account options."
    },
    {
      icon: Lock,
      title: "Secure Banking",
      description: "Your security is our priority. Advanced encryption and fraud protection keep your money safe."
    }
  ];

  const accountTypes = [
    {
      name: "Basic Savings Account",
      features: ["No minimum balance", "Free online banking", "Debit card included", "24/7 customer support"],
      image: "https://images.pexels.com/photos/1602726/pexels-photo-1602726.jpeg"
    },
    {
      name: "Premium Checking Account",
      features: ["Higher interest rates", "Free ATM withdrawals", "Premium rewards", "Personal financial advisor"],
      image: "https://images.unsplash.com/photo-1681826291722-70bd7e9e6fc3"
    },
    {
      name: "Youth Savings Account",
      features: ["For ages 13-25", "No monthly fees", "Financial literacy resources", "Special youth benefits"],
      image: "https://images.pexels.com/photos/4968391/pexels-photo-4968391.jpeg"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6">Personal Banking</h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                From savings and current accounts to digital banking solutions, we provide secure and convenient financial services designed to support your everyday needs and future goals.
              </p>
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg">
                Open an Account
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1609921141835-710b7fa6e438"
                alt="Mobile Banking"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Banking Services for You</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Comprehensive personal banking solutions designed to make your life easier
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="bg-white hover:shadow-xl transition-all duration-300 group border-0 shadow-md">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-600 transition-colors duration-300">
                    <service.icon className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Account Types Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Account Type</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We offer a range of account options to suit your financial needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {accountTypes.map((account, index) => (
              <Card key={index} className="bg-white overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                <img
                  src={account.image}
                  alt={account.name}
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{account.name}</h3>
                  <ul className="space-y-3 mb-6">
                    {account.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Banking Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1599202875854-23b7cd490ff4"
                alt="Digital Banking"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Digital Banking Made Simple</h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Experience the future of banking with our intuitive digital platform. Manage your finances, make payments, and track your spending all in one place.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Instant Transfers</h4>
                    <p className="text-gray-600">Send money to friends and family instantly</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Bill Payments</h4>
                    <p className="text-gray-600">Pay all your bills in one convenient location</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Financial Insights</h4>
                    <p className="text-gray-600">Track spending and manage budgets with ease</p>
                  </div>
                </li>
              </ul>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3">
                Download App
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Open your personal banking account today and experience banking that works for you
          </p>
          <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg">
            Open Account Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Personal;
